import { pageFrameStyle } from '../../../../components/layout/PageFrame';
import { LuGlobe, LuMapPin, LuMessageSquareWarning, LuChevronRight } from 'react-icons/lu';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { FiChevronLeft, FiMoreHorizontal, FiX } from 'react-icons/fi';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import RecommendationFeedback from '../../../../features/discovery/RecommendationFeedback';
import './AlbumDetailHeader.css';

export default function AlbumDetailHeader({ album, isAdmin, onRegionChange }) {
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const navigate = useNavigate();
  const [topActions, setTopActions] = useState(() => window.scrollY < 8);
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionBusy, setRegionBusy] = useState(false);
  const regionLock = useRef(false);
  const [regionError, setRegionError] = useState(false);
  useEffect(() => {
    setTopActions(window.scrollY < 8);
    const onScroll = () => {
      // Build58 native navigation uses separate 8px hide / 4px restore thresholds.
      setTopActions((shown) => (shown ? window.scrollY < 8 : window.scrollY <= 4));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [album?.albumSource, album?.sourceId, album?.id]);
  const back = () => {
    // Router-owned history only: direct/external entry falls back inside Postpop.
    if (Number(window.history.state?.idx) > 0) navigate(-1);
    else navigate('/explore', { replace: true });
  };
  const changeRegion = async (region) => {
    if (regionLock.current || !isAdmin) return;
    regionLock.current = true;
    setRegionBusy(true);
    setRegionError(false);
    try {
      await onRegionChange(region);
      setRegionOpen(false);
    } catch {
      setRegionError(true);
    } finally {
      regionLock.current = false;
      setRegionBusy(false);
    }
  };
  const showTopActions = !album || topActions;
  return (
    <>
      <header
        className={`pp-album-header${album?.imageUrl ? ' has-artwork' : ''}${showTopActions ? ' has-top-actions' : ''}`}
        aria-label={en ? 'Album navigation' : '앨범 탐색'}
      >
        <div className="pp-album-header-inner pp-page-frame" style={pageFrameStyle}>
          <button
            className="pp-album-top-action pp-album-back"
            type="button"
            onClick={back}
            aria-label={en ? 'Back' : '뒤로가기'}
            aria-hidden={!showTopActions}
            tabIndex={showTopActions ? 0 : -1}
            disabled={!showTopActions}
          >
            <FiChevronLeft size={27} strokeWidth={2.4} />
          </button>
          {album && (
            <RecommendationFeedback
              key={`${album.albumSource}:${album.sourceId}:${album.id}`}
              item={album}
              albumDetail
              event={{
                context: 'album_detail_recommendation_feedback',
                surface: 'ALBUM_DETAIL',
                section: 'ALBUM_OPTIONS',
              }}
              renderTrigger={(open) => (
                <button
                  type="button"
                  className="pp-album-top-action pp-album-more"
                  aria-hidden={!showTopActions}
                  tabIndex={showTopActions ? 0 : -1}
                  disabled={!showTopActions}
                  aria-label={en ? 'Recommendation options' : '추천 설정'}
                  onClick={open}
                >
                  <FiMoreHorizontal size={20} />
                </button>
              )}
              extraActions={(close, busy) => (
                <>
                  {isAdmin && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        close();
                        setRegionOpen(true);
                      }}
                    >
                      <LuGlobe size={20} />
                      <span>
                        {en ? 'Correct Album Region' : '국내·해외 분류 보정'}
                        <small>
                          {album.displayRegion === 'KR'
                            ? en
                              ? 'Korean Album'
                              : '국내 앨범'
                            : album.displayRegion === 'GLOBAL'
                              ? en
                                ? 'Global Album'
                                : '해외 앨범'
                              : en
                                ? 'Unclassified'
                                : '미분류'}
                        </small>
                      </span>
                      <LuChevronRight size={20} />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      close();
                      navigate('/contact', {
                        state: {
                          kind: 'albumCorrection',
                          albumTitle: album.title,
                          artistName: album.artist?.name,
                          albumPath: album.slug || `local/${album.id}`,
                        },
                      });
                    }}
                  >
                    <LuMessageSquareWarning size={20} />
                    <span>{en ? 'Suggest an Album Correction' : '앨범 정보 수정 제안'}</span>
                    <LuChevronRight size={20} />
                  </button>
                </>
              )}
            />
          )}
        </div>
      </header>
      {isAdmin && (
        <Dialog
          open={regionOpen}
          onClose={() => {
            if (!regionLock.current) setRegionOpen(false);
          }}
          className="pp-album-options"
          fullWidth
          maxWidth="xs"
          aria-labelledby="album-region-title"
        >
          <DialogTitle id="album-region-title">
            {en ? 'Correct Album Region' : '앨범 분류 보정'}
            <button
              type="button"
              disabled={regionBusy}
              aria-label={en ? 'Close' : '닫기'}
              onClick={() => setRegionOpen(false)}
            >
              <FiX size={20} />
            </button>
          </DialogTitle>
          <DialogContent>
            <div className="pp-feedback-options">
              {['KR', 'GLOBAL'].map((region) => (
                <button
                  type="button"
                  key={region}
                  disabled={regionBusy}
                  aria-pressed={album?.displayRegion === region}
                  onClick={() => changeRegion(region)}
                >
                  {region === 'KR' ? <LuMapPin size={20} /> : <LuGlobe size={20} />}
                  <span>
                    {region === 'KR'
                      ? en
                        ? 'Korean Album'
                        : '국내 앨범'
                      : en
                        ? 'Global Album'
                        : '해외 앨범'}
                    <small>
                      {region === 'KR'
                        ? en
                          ? 'Show this album in Korean discovery and timelines.'
                          : '국내 탐색과 타임라인에 표시합니다.'
                        : en
                          ? 'Show this album in global discovery and timelines.'
                          : '해외 탐색과 타임라인에 표시합니다.'}
                    </small>
                  </span>
                </button>
              ))}
              {regionError && (
                <p role="alert">
                  {en
                    ? 'Could not correct the album region. Please try again.'
                    : '앨범 분류를 보정하지 못했습니다. 다시 시도해 주세요.'}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
